import React, { Component } from 'react';
import { Form, Button, Segment, Icon } from 'semantic-ui-react';
import ValidatedInput from '../../components/common/validatedInput';
import { roles, getRoleOptions, roleToContextType } from '../../constants/roles';
import { isSystemOwner as checkIsSystemOwner } from '../../modules/session';
import { markSystemOnlyOptions } from '../../components/common/SystemOnlyBadge';
import { connect } from 'react-redux';
import {
  onInviteAdminParamChange,
  inviteAdmin,
  getOrgOptions,
  onInvitePanelValidationChange,
  getCampaignOptions,
  getEventOptions,
  getEventOrganizerOptions,
  getCampaignOrganizerOptions
} from './store/accountActions';
import { Form as CommonForm } from '../../components';
import {
  paths,
  getInviteAdminValidation,
  getInviteValidationModels,
  toEntityIds
} from './constants';
import './index.css';
import { I18n } from 'react-redux-i18n';
import { Record } from '../../modules/bin/utility';
import get from 'lodash.get';
import * as validationHelper from '../../helpers/validationHelper';
import {
  getCampaignOption,
  getEntityOption,
  getOrganizationOption
} from '../../helpers/entityOptionHelper';

export class InviteAdmin extends Component {
  componentDidUpdate() {
    if (
      this.props.isOrganizationAdmin &&
      !this.props.inviteAdmin.params.organizationId
    ) {
      this.onOrganizationChange(undefined, {
        value: get(this.props, 'organization.id')
      });
    }
  }

  componentWillReceiveProps = (nextProps) => {
    const isAllFieldsValid = validationHelper.isAllFieldsValid(
      getInviteValidationModels(nextProps.inviteAdmin.params)
    );

    if (nextProps.isInvitePanelValid !== isAllFieldsValid) {
      this.props.onInvitePanelValidationChange(isAllFieldsValid);
    }
  };

  onInputChange = (path, value) => {
    this.props.onInviteAdminParamChange(path, value);
  };

  onInviteClick = () => {
    let { params } = this.props.inviteAdmin;
    const { roleDetail = {} } = params;
    this.props.onInviteAdminClick({
      asInvite: true,
      firstName: params.firstName,
      lastName: params.lastName,
      email: params.email,
      onboarding: {
        // The System option carries its context type directly; the six legacy options are still
        // derived from the roles mask they were built on.
        contextType: params.contextType || roleToContextType[params.roles],
        restrictExports: roleDetail.restrictExports || null,
        organizationId: params.organizationId || null,
        campaignId: roleDetail.campaignId || null,
        eventId: roleDetail.eventId || null,
        eventOrganizerId: roleDetail.eventOrganizerId || null,
        campaignOrganizerId: roleDetail.campaignOrganizerId || null
      }
    });
  };

  onOrganizationSearch = (event, searchTerm) => {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.props.getOrgOptions(searchTerm.searchQuery);
    }, 500);
  };

  onCampaignSearch = (event, searchTerm) => {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.props.getCampaignOptions(searchTerm.searchQuery);
    }, 500);
  };

  onEventSearch = (event, searchTerm) => {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.props.getEventOptions(searchTerm.searchQuery);
    }, 500);
  };

  onEventOrganizerSearch = (event, searchTerm) => {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.props.getEventOrganizerOptions(searchTerm.searchQuery);
    }, 500);
  };

  onCampaignOrganizerSearch = (campaignOrganizer, searchTerm) => {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.props.getCampaignOrganizerOptions(searchTerm.searchQuery);
    }, 500);
  };

  organizationClear = () => {
    this.props.getOrgOptions('');
  };

  campaignClear = () => {
    this.props.getCampaignOptions('');
  };

  eventClear = () => {
    this.props.getEventOptions('');
  };

  eventOrganizerClear = () => {
    this.props.getEventOrganizerOptions('');
  };

  campaignOrganizerClear = () => {
    this.props.getCampaignOrganizerOptions('');
  };

  onOrganizationChange = (evt, obj) => {
    this.onInputChange(paths.INVITE_ADMIN_ORGANIZATION, obj.value);
  };

  onCampaignChange = (evt, obj) => {
    this.onInputChange(paths.INVITE_ADMIN_CAMPAIGN, toEntityIds(obj.value));
  };

  onEventChange = (evt, obj) => {
    this.onInputChange(paths.INVITE_ADMIN_EVENT, toEntityIds(obj.value));
  };

  onEventOrganizerChange = (evt, obj) => {
    this.onInputChange(
      paths.INVITE_ADMIN_EVENT_ORGANIZER,
      toEntityIds(obj.value)
    );
  };

  onCampaignOrganizerChange = (evt, obj) => {
    this.onInputChange(
      paths.INVITE_ADMIN_CAMPAIGN_ORGANIZER,
      toEntityIds(obj.value)
    );
  };

  // An entity picked for the previous role means nothing to the new one, and would otherwise be
  // sent alongside it. The organisation an org admin is pinned to is not a selection of theirs,
  // so it is left for componentDidUpdate to keep.
  clearEntitySelections = () => {
    if (!this.props.isOrganizationAdmin) {
      this.onInputChange(paths.INVITE_ADMIN_ORGANIZATION, '');
    }
    this.onInputChange(paths.INVITE_ADMIN_CAMPAIGN, toEntityIds(undefined));
    this.onInputChange(paths.INVITE_ADMIN_EVENT, toEntityIds(undefined));
    this.onInputChange(
      paths.INVITE_ADMIN_EVENT_ORGANIZER,
      toEntityIds(undefined)
    );
    this.onInputChange(
      paths.INVITE_ADMIN_CAMPAIGN_ORGANIZER,
      toEntityIds(undefined)
    );
  };

  handleRoleChange = (e, value) => {
    const selectedValue = value.options.find((x) => x.value === value.value);
    this.clearEntitySelections();
    this.onInputChange(paths.INVITE_ADMIN_ROLE_NAME, value.value);
    this.onInputChange(paths.INVITE_ADMIN_ROLE, selectedValue.definition.role);
    this.onInputChange(paths.INVITE_ADMIN_CONTEXT_TYPE, selectedValue.definition.contextType);
    this.onInputChange(paths.INVITE_ADMIN_RESTRICT_EXPORTS, selectedValue.definition.restrictExports);
  };

  getOrgOptions = () => {
    let options = [];
    const { list } = this.props.inviteAdmin.orgOptions.data;
    if (list) {
      options = list.map((x) => this.getOrganizationItem(x));
    }

    return options;
  };

  getCampaignOptions = () => {
    let options = [];
    const { list } = this.props.inviteAdmin.campaignOptions.data;
    if (list) {
      options = list.map((x) => this.getCampaignItem(x));
    }

    return options;
  };

  getEventOptions = () => {
    let options = [];
    const { list } = this.props.inviteAdmin.eventOptions.data;
    if (list) {
      options = list.map((x) => this.getEventItem(x));
    }

    return options;
  };

  getEventOrganizerOptions = () => {
    let options = [];
    const { list } = this.props.inviteAdmin.eventOrganizerOptions.data;
    if (list) {
      options = list.map((x) => this.getEventOrganizerItem(x));
    }

    return options;
  };

  getCampaignOrganizerOptions = () => {
    let options = [];
    const { list } = this.props.inviteAdmin.campaignOrganizerOptions.data;
    if (list) {
      options = list.map((x) => this.getCampaignOrganizerItem(x));
    }

    return options;
  };

  getOrganizationItem = (item) => getOrganizationOption(item);

  getCampaignItem = (item) => getCampaignOption(item);

  getEventItem = (item) => getEntityOption(item);

  getEventOrganizerItem = (item) => getEntityOption(item);

  getCampaignOrganizerItem = (item) => getEntityOption(item);

  render() {
    if (!this.props.inviteAdmin.isPanelVisible) {
      return null;
    }

    const { params } = this.props.inviteAdmin;

    const orgOptions = this.getOrgOptions();
    const campaignOptions = this.getCampaignOptions();
    const eventOptions = this.getEventOptions();
    const eventOrganizerOptions = this.getEventOrganizerOptions();
    const campaignOrganizerOptions = this.getCampaignOrganizerOptions();
    const inviteAdminValidation = getInviteAdminValidation(params.roles);

    return (
      <Segment style={{ backgroundColor: 'rgba(216, 216, 216, 0.05)' }}>
        <Form>
          <Icon
            link
            className="right-float"
            name="close"
            onClick={this.props.onDismissClick}
          />
          <Form.Group>
            <Form.Field width={4}>
              <ValidatedInput
                id="invitePanelFirstName"
                label={I18n.t('account.invite-admin.first-name')}
                value={params.firstName}
                onChange={(e, { value }) => {
                  this.onInputChange(paths.INVITE_ADMIN_FIRST_NAME, value);
                }}
                validation={inviteAdminValidation.FIRST_NAME}
              />
            </Form.Field>
            <Form.Field width={4}>
              <ValidatedInput
                id="invitePanelLastName"
                label={I18n.t('account.invite-admin.last-name')}
                value={params.lastName}
                onChange={(e, { value }) => {
                  this.onInputChange(paths.INVITE_ADMIN_LAST_NAME, value);
                }}
                validation={inviteAdminValidation.LAST_NAME}
              />
            </Form.Field>
            <Form.Field width={4}>
              <ValidatedInput
                id="invitePanelEmail"
                label={I18n.t('account.invite-admin.email')}
                value={params.email}
                validation={inviteAdminValidation.EMAIL}
                onChange={(e, { value }) => {
                  this.onInputChange(paths.INVITE_ADMIN_EMAIL, value);
                }}
              />
            </Form.Field>

          </Form.Group>
          <Form.Group className='invite-panel-fields'>
            <Form.Select
              id="invitePanelRole"
              fluid
              label={I18n.t('account.invite-admin.role')}
              options={markSystemOnlyOptions(
                getRoleOptions(
                  this.props.isSystemAdmin,
                  this.props.isOrganizationAdmin,
                  this.props.isSystemOwner
                ).filter((option) => option.isVisible)
              )}
              value={params.roleName}
              width={4}
              required={inviteAdminValidation.ROLE.required}
              onChange={this.handleRoleChange}
            />

            {!this.props.isOrganizationAdmin &&
              params.roles === roles.ORGANIZATION_ADMIN && (
                <CommonForm.Autocomplete
                  id="invitePanelOrganization"
                  label="Organization"
                  value={params.organizationId}
                  onChange={this.onOrganizationChange}
                  search={this.onOrganizationSearch}
                  options={orgOptions}
                  validation={inviteAdminValidation.ORGANIZATION}
                  loading={Record.isRecordLoading(
                    this.props.inviteAdmin.orgOptions
                  )}
                  clear={this.organizationClear}
                />
              )}

            {params.roles === roles.CAMPAIGN_ADMIN && (
              <CommonForm.Autocomplete
                id="invitePanelCampaign"
                label="Campaign"
                value={get(params, `${paths.INVITE_ADMIN_CAMPAIGN}[0]`)}
                onChange={this.onCampaignChange}
                search={this.onCampaignSearch}
                options={campaignOptions}
                validation={inviteAdminValidation.CAMPAIGN}
                loading={Record.isRecordLoading(
                  this.props.inviteAdmin.campaignOptions
                )}
                clear={this.campaignClear}
              />
            )}

            {params.roles === roles.EVENT_ADMIN && (
              <CommonForm.Autocomplete
                id="invitePanelEvent"
                label="Event"
                value={get(params, `${paths.INVITE_ADMIN_EVENT}[0]`)}
                onChange={this.onEventChange}
                search={this.onEventSearch}
                options={eventOptions}
                validation={inviteAdminValidation.EVENT}
                loading={Record.isRecordLoading(
                  this.props.inviteAdmin.eventOptions
                )}
                clear={this.eventClear}
              />
            )}

            {params.roles === roles.EVENT_ORGANIZER_ADMIN && (
              <CommonForm.Autocomplete
                id="invitePanelEventOrganizer"
                label="Event organizer"
                value={get(params, `${paths.INVITE_ADMIN_EVENT_ORGANIZER}[0]`)}
                onChange={this.onEventOrganizerChange}
                search={this.onEventOrganizerSearch}
                options={eventOrganizerOptions}
                validation={inviteAdminValidation.EVENT_ORGANIZER}
                loading={Record.isRecordLoading(
                  this.props.inviteAdmin.eventOrganizerOptions
                )}
                clear={this.eventOrganizerClear}
              />
            )}

            {params.roles === roles.CAMPAIGN_ORGANIZER_ADMIN && (
              <CommonForm.Autocomplete
                id="invitePanelCampaignOrganizer"
                label="Campaign organiser"
                value={get(
                  params,
                  `${paths.INVITE_ADMIN_CAMPAIGN_ORGANIZER}[0]`
                )}
                onChange={this.onCampaignOrganizerChange}
                search={this.onCampaignOrganizerSearch}
                options={campaignOrganizerOptions}
                validation={inviteAdminValidation.CAMPAIGN_ORGANIZER}
                loading={Record.isRecordLoading(
                  this.props.inviteAdmin.campaignOrganizerOptions
                )}
                clear={this.campaignOrganizerClear}
              />
            )}

            <Form.Field width={3}>
              <div className="field">
                <label className="input-sub-bottom">&nbsp;</label>
                <Button
                  id="invitePanelInviteButton"
                  primary
                  onClick={this.onInviteClick}
                  loading={Record.isRecordLoading(this.props.inviteAdminRecord)}
                  disabled={
                    Record.isRecordLoading(this.props.inviteAdminRecord) ||
                    !this.props.isInvitePanelValid
                  }
                >
                  {I18n.t('account.invite-admin.invite-button')}
                </Button>
              </div>
            </Form.Field>
          </Form.Group>
        </Form>
      </Segment>
    );
  }
}

const mapState = ({ account, session }) => {
  return {
    inviteAdmin: account.inviteAdmin,
    inviteAdminRecord: account.inviteAdminRecord,
    isInvitePanelValid: account.isInvitePanelValid,
    isOrganizationAdmin: session.isOrganizationAdmin,
    isSystemAdmin: session.isSystemAdmin,
    // Inviting system owners is not part of this work, and only an owner may grant a system
    // membership, so the option is theirs alone.
    isSystemOwner: checkIsSystemOwner(session),
    organization: session.organization
  };
};

const mapDispatch = (dispatch) => {
  return {
    onInviteAdminParamChange(path, value) {
      dispatch(onInviteAdminParamChange(path, value));
    },
    onInviteAdminClick(record) {
      dispatch(inviteAdmin(record));
    },
    getOrgOptions(searchtext) {
      dispatch(getOrgOptions(searchtext));
    },
    getCampaignOptions(searchtext) {
      dispatch(getCampaignOptions(searchtext));
    },
    getEventOptions(searchtext) {
      dispatch(getEventOptions(searchtext));
    },
    getEventOrganizerOptions(searchtext) {
      dispatch(getEventOrganizerOptions(searchtext));
    },
    getCampaignOrganizerOptions(searchtext) {
      dispatch(getCampaignOrganizerOptions(searchtext));
    },
    onInvitePanelValidationChange(value) {
      dispatch(onInvitePanelValidationChange(value));
    }
  };
};
export default connect(mapState, mapDispatch)(InviteAdmin);
